import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, ExternalLink, GitBranch, Github, Loader2, Plus, RefreshCw, Search, Share2, X } from 'lucide-react';
import { apiService } from '../../services/api';

// Code du projet sur GitHub : les dépôts reliés et les derniers commits relevés. Chaque nuit, Deepflow relève les
// nouveaux commits de toutes les branches ; Muse les lit avec le reste du projet et met au carnet (parfois au journal)
// ce qu'ils font avancer (backend : app/services/github_code.py). Un dépôt d'un autre compte (projet annexe,
// organisation d'un client) se partage par l'application GitHub « Deepflow Nexus », installée par la personne
// elle-même sur les seuls dépôts qu'elle choisit, en lecture seule.

const when = (iso) => (iso ? new Intl.DateTimeFormat('fr-FR', {
  day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
}).format(new Date(iso.endsWith('Z') || iso.includes('+') ? iso : `${iso}Z`)) : '');

const CodeDialog = ({ projectId, open, onClose, onChange }) => {
  const [data, setData] = useState(null);
  const [query, setQuery] = useState('');
  const [repos, setRepos] = useState(null);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(() => apiService.get(`/projects/${projectId}/github`)
    .then((result) => { setData(result); onChange?.(result.links || []); })
    .catch((err) => setError(err.message)), [projectId, onChange]);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setData(null);
    load();
  }, [open, load]);

  const ready = Boolean(data?.configured || data?.app_configured);

  useEffect(() => {
    if (!open || !ready) return undefined;
    const timer = setTimeout(() => {
      apiService.get(`/projects/${projectId}/github/available?q=${encodeURIComponent(query)}`)
        .then(setRepos).catch((err) => setError(err.message));
    }, 250);
    return () => clearTimeout(timer);
  }, [open, ready, query, projectId, data?.links?.length]);

  if (!open) return null;

  const act = async (key, request) => {
    setBusy(key);
    setError(null);
    try {
      await request();
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(null);
    }
  };
  const link = (r) => act(r.repo, () => apiService.post(`/projects/${projectId}/github`, {
    repo: r.repo, installation_id: r.installation_id || null }));
  const share = async () => {
    setBusy('share');
    try {
      const { url } = await apiService.get(`/projects/${projectId}/github/install`);
      window.location.assign(url);
    } catch (err) {
      setError(err.message);
      setBusy(null);
    }
  };
  const unlink = (repo) => act(`-${repo}`, () => apiService.delete(`/projects/${projectId}/github?repo=${encodeURIComponent(repo)}`));
  const sync = () => act('sync', async () => {
    await apiService.post(`/projects/${projectId}/github/sync`, {});
    await new Promise((resolve) => { setTimeout(resolve, 4000); });
  });

  const links = data?.links || [];
  const recent = data?.recent || [];

  return createPortal(
    <div className="fixed inset-0 z-[70] flex items-end justify-center bg-slate-900/40 p-0 sm:items-center sm:p-4"
      onClick={(event) => { event.stopPropagation(); onClose(); }}>
      <div className="max-h-[92vh] w-full max-w-lg overflow-y-auto overscroll-contain rounded-t-2xl bg-white p-5 text-slate-800 shadow-2xl sm:rounded-2xl"
        onClick={(event) => event.stopPropagation()}>
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <h2 className="flex items-center gap-2 text-lg font-semibold text-slate-900"><Github size={18} /> Code du projet</h2>
            <p className="text-xs text-slate-500">
              Chaque nuit, Nexus relève les nouveaux commits de toutes les branches ; Muse met au carnet (et au journal si
              ça compte) ce qu&apos;ils font avancer. Le code reste chez Innowide.
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label="Fermer" className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700">
            <X size={18} />
          </button>
        </div>

        {error && <p className="mb-3 flex items-start gap-2 rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700"><AlertTriangle size={15} className="mt-0.5 shrink-0" />{error}</p>}
        {!data && !error && <p className="flex items-center gap-2 text-sm text-slate-500"><Loader2 size={15} className="animate-spin" /> Chargement…</p>}
        {data && !ready && (
          <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
            GitHub n&apos;est pas encore relié à Deepflow : il manque le jeton d&apos;accès en lecture sur le serveur.
          </p>
        )}

        {data && (
          <section className="space-y-2">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Dépôts reliés</h3>
            {links.length === 0 && <p className="text-sm text-slate-400">Aucun dépôt relié pour l&apos;instant.</p>}
            {links.map((l) => (
              <div key={l.repo} className="flex items-center gap-3 rounded-xl border border-slate-200 px-3 py-2">
                <div className="min-w-0 flex-1">
                  <a href={l.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 truncate text-sm font-semibold text-slate-800 hover:text-indigo-700">
                    {l.repo} <ExternalLink size={12} className="shrink-0 text-slate-400" />
                  </a>
                  <p className={`text-xs ${l.error ? 'text-rose-600' : 'text-slate-500'}`}>
                    {l.error || (l.synced_at ? `Relevé le ${when(l.synced_at)} · ${l.commits_7_days} commit${l.commits_7_days > 1 ? 's' : ''} sur 7 jours` : 'Premier relevé en cours…')}
                  </p>
                  {l.shared_from && <p className="text-[11px] text-indigo-600">Partagé depuis {l.shared_from}{l.shared_by ? ` par ${l.shared_by}` : ''}</p>}
                </div>
                <button type="button" onClick={() => unlink(l.repo)} disabled={busy === `-${l.repo}`}
                  className="shrink-0 rounded-lg px-2 py-1 text-xs font-semibold text-slate-500 hover:bg-rose-50 hover:text-rose-700 disabled:opacity-50">
                  Retirer
                </button>
              </div>
            ))}
            {links.length > 0 && ready && (
              <button type="button" onClick={sync} disabled={busy === 'sync'}
                className="flex items-center gap-1.5 text-xs font-semibold text-indigo-600 hover:text-indigo-800 disabled:opacity-60">
                <RefreshCw size={13} className={busy === 'sync' ? 'animate-spin' : ''} /> Relever maintenant
              </button>
            )}
          </section>
        )}

        {ready && (
          <section className="mt-5 space-y-2">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Relier un dépôt</h3>
            <label className="flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 focus-within:border-indigo-300">
              <Search size={15} className="text-slate-400" />
              <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Chercher un dépôt…"
                className="min-w-0 flex-1 bg-transparent text-sm outline-none" />
            </label>
            <div className="max-h-52 space-y-1 overflow-y-auto">
              {repos === null && <p className="flex items-center gap-2 px-1 text-sm text-slate-400"><Loader2 size={14} className="animate-spin" /> Dépôts…</p>}
              {repos?.length === 0 && <p className="px-1 text-sm text-slate-400">Aucun dépôt trouvé.</p>}
              {(repos || []).slice(0, 12).map((r) => (
                <button key={r.repo} type="button" onClick={() => link(r)} disabled={busy === r.repo}
                  className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left hover:bg-indigo-50 disabled:opacity-60">
                  {busy === r.repo ? <Loader2 size={14} className="animate-spin text-indigo-500" /> : <Plus size={14} className="text-indigo-500" />}
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-slate-800">{r.repo}</span>
                    {r.description && <span className="block truncate text-xs text-slate-500">{r.description}</span>}
                  </span>
                  {r.installation_id && <span className="shrink-0 rounded-full bg-indigo-50 px-2 py-0.5 text-[10px] font-semibold text-indigo-600">partagé par toi</span>}
                </button>
              ))}
            </div>
            {data.app_configured && (
              <div className="rounded-xl border border-dashed border-slate-200 px-3 py-2.5">
                <button type="button" onClick={share} disabled={busy === 'share'}
                  className="flex items-center gap-1.5 text-sm font-semibold text-indigo-600 hover:text-indigo-800 disabled:opacity-60">
                  {busy === 'share' ? <Loader2 size={14} className="animate-spin" /> : <Share2 size={14} />} Ajouter un dépôt d&apos;un autre compte GitHub
                </button>
                <p className="mt-1 text-xs text-slate-500">
                  Projet annexe, organisation d&apos;un client : sur GitHub, tu choisis toi-même les dépôts partagés, en lecture
                  seule, et tu peux retirer l&apos;accès à tout moment. Leurs commits seront visibles par l&apos;équipe sur ce projet.
                </p>
              </div>
            )}
          </section>
        )}

        {recent.length > 0 && (
          <section className="mt-5 space-y-2">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Derniers commits relevés</h3>
            <ul className="space-y-1.5">
              {recent.slice(0, 15).map((c) => (
                <li key={`${c.repo}-${c.sha}`} className="rounded-lg bg-slate-50 px-3 py-2">
                  <a href={c.url} target="_blank" rel="noopener noreferrer" className="block text-sm text-slate-800 hover:text-indigo-700">
                    {(c.message || '').split('\n')[0]}
                  </a>
                  <p className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-slate-500">
                    <span>{c.author}</span>
                    <span>{when(c.at)}</span>
                    {c.branch && <span className="inline-flex items-center gap-0.5"><GitBranch size={11} />{c.branch}</span>}
                    <span className="tabular-nums text-emerald-600">+{c.additions}</span>
                    <span className="tabular-nums text-rose-600">−{c.deletions}</span>
                  </p>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </div>,
    document.body,
  );
};

export default CodeDialog;
